import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Header from '../../shared/components/Header';
import Footer from '../../shared/components/Footer';
import { fetchPokemonList } from '../../shared/api/pokemon';
import { createSoloDraft } from '../../shared/api/draft';
import { Pokemon } from '../../types';
import { findMenuIndex } from '../Emulator/notebookPokemonList';
import { generateRandomDraftName } from '../../shared/utils/draftNameGenerator';
import { getIconName } from '../../utils/speciesUtils';
import { getPrimaryEggMove } from '../../utils/eggMovesData';
import './SinglePlayerPage.scss';

function formatPokemonName(name: string): string {
  const lower = name.toLowerCase();
  if (lower.startsWith("farfetch'd")) {
    return "farfetch'd";
  }
  return lower.replace(/'/g, '');
}

const getTypeIconSrc = (type: string): string => {
  const formattedType = type.charAt(0).toUpperCase() + type.slice(1).toLowerCase();
  return `/TypeIcons/${formattedType}IC_SV.png`;
};

function getPokemonKey(p: Pokemon): string {
  return `${p.pokedex_id ?? p.id}-${p.form ?? ''}`;
}

// Unbiased Fisher-Yates (Knuth) shuffle algorithm
function shuffleArray<T>(array: T[]): T[] {
  const result = [...array];
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    const temp = result[i];
    result[i] = result[j];
    result[j] = temp;
  }
  return result;
}

function calculateBst(mon: Pokemon): number {
  if (!mon.stats) return 0;
  const s = mon.stats;
  const spa = s.sp_attack ?? s.specialAttack ?? 0;
  const spd = s.sp_defense ?? s.specialDefense ?? 0;
  return s.hp + s.attack + s.defense + spa + spd + s.speed;
}

function getPokemonImageSrc(pokemon: Pokemon): string {
  const nameForImg = pokemon.name.includes('(')
    ? pokemon.name.match(/\(([^)]+)\)/)?.[1] || pokemon.name
    : pokemon.name;

  if (nameForImg.startsWith('Basculegion-') && (pokemon.form === 'M' || pokemon.form === 'F')) {
    const gender = pokemon.form === 'M' ? 'Male' : 'Female';
    return `/evolutions/Basculegion-${gender}.png`;
  }

  if (pokemon.evolves_from_id && (pokemon.form === 'Alola' || pokemon.form === 'Galar' || pokemon.form === 'Hisui' || pokemon.form === 'Paldea')) {
    return `/evolutions/${nameForImg}-${pokemon.form}.png`;
  }

  if (pokemon.form === 'Mega' || (pokemon.name.includes('Mega') && !pokemon.name.includes('Meganium'))) {
    const baseName = nameForImg.startsWith('Mega ')
      ? nameForImg.slice(5)
      : nameForImg;
    return `/evolutions/${baseName}-Mega.png`;
  } else if (nameForImg.includes('Charizard') && pokemon.form === 'Mega X') {
    return `/evolutions/Charizard X-Mega.png`;
  } else if (pokemon.evolves_from_id) {
    return `/evolutions/${nameForImg}.png`;
  } else {
    return `/baseforms/${nameForImg}.png`;
  }
}

function getEvolutionLine(basePokemon: Pokemon, allPokemon: Pokemon[]): Pokemon[] {
  if (!allPokemon || allPokemon.length === 0) return [basePokemon];

  // If basePokemon evolves from something, find its true base form
  let root = basePokemon;
  let curr = basePokemon;
  let guard = 0;
  while (curr.evolves_from_id && guard < 10) {
    guard++;
    const parent = allPokemon.find(
      (p) =>
        (p.pokedex_id ?? p.id).toString() === curr.evolves_from_id?.toString() &&
        (p.form ?? '') === (curr.evolves_from_form ?? '')
    );
    if (!parent || parent === curr) break;
    root = parent;
    curr = parent;
  }

  const seen = new Set<string>();
  const line: Pokemon[] = [];

  function collect(node: Pokemon) {
    const key = `${node.pokedex_id ?? node.id}-${node.form ?? ''}`;
    if (seen.has(key)) return;
    seen.add(key);
    line.push(node);

    // Find all direct children
    const children = allPokemon
      .filter((p) => {
        const idMatches = p.evolves_from_id?.toString() === (node.pokedex_id ?? node.id).toString();
        if (!idMatches) return false;
        const pEvoForm = p.evolves_from_form ?? '';
        const nodeForm = node.form ?? '';
        return pEvoForm === nodeForm || (pEvoForm === '' && (nodeForm === 'base' || !node.form));
      })
      .sort((a, b) => (a.pokedex_id ?? a.id ?? 0) - (b.pokedex_id ?? b.id ?? 0));

    for (const child of children) {
      collect(child);
    }
  }

  collect(root);
  return line;
}

interface PokemonDraftCardProps {
  mon: Pokemon;
  allPokemon: Pokemon[];
  index: number;
  isSelected: boolean;
  selectOrder: number;
  onToggleSelect: (mon: Pokemon) => void;
}

const PokemonDraftCard: React.FC<PokemonDraftCardProps> = ({
  mon,
  allPokemon,
  index,
  isSelected,
  selectOrder,
  onToggleSelect,
}) => {
  const [activeMon, setActiveMon] = useState<Pokemon>(mon);

  useEffect(() => {
    setActiveMon(mon);
  }, [mon]);

  const evoLine = useMemo(() => {
    return getEvolutionLine(mon, allPokemon);
  }, [mon, allPokemon]);

  const hasEvolutions = evoLine.length > 1;
  const isViewingEvolution =
    (activeMon.pokedex_id ?? activeMon.id) !== (mon.pokedex_id ?? mon.id) ||
    (activeMon.form ?? '') !== (mon.form ?? '');
  const bst = calculateBst(activeMon);
  const primaryEgg = useMemo(() => {
    return getPrimaryEggMove(activeMon.name, activeMon.pokedex_id ?? activeMon.id);
  }, [activeMon.name, activeMon.pokedex_id, activeMon.id]);

  return (
    <div
      className={`pokemon-card ${isSelected ? 'selected' : ''} ${hasEvolutions ? 'has-evo-sidebar' : ''}`}
      onClick={() => onToggleSelect(mon)}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onToggleSelect(mon);
        }
      }}
    >
      <div className="card-main-content">
        {/* Top Row: Index Badge, Preview Tag & Selection Badge */}
        <div className="card-top-row">
          <div className="card-top-left">
            <span className="card-pool-number">#{index + 1}</span>
            {isViewingEvolution && (
              <span className="card-evo-preview-tag" title="Viewing evolution stats (Drafts base form)">
                Evo Preview
              </span>
            )}
          </div>
          {isSelected ? (
            <span className="card-pick-badge">
              ✓ Pick #{selectOrder}
            </span>
          ) : (
            <span className="card-pokedex-number">
              #{activeMon.pokedex_id ?? activeMon.id}
            </span>
          )}
        </div>

        {/* Sprite Image Container */}
        <div className="card-sprite-wrapper">
          <img
            key={getPokemonKey(activeMon)}
            className="card-sprite"
            src={getPokemonImageSrc(activeMon)}
            alt={activeMon.name}
            onError={(e) => {
              const target = e.target as HTMLImageElement;
              const icon = getIconName(activeMon.name, undefined, activeMon.form);
              if (!target.src.includes('/baseforms/')) {
                target.src = `/baseforms/${activeMon.name}.png`;
              } else if (!target.src.includes('/MiniIcons/')) {
                target.src = `/MiniIcons/${icon}.png`;
              } else {
                target.src = '/MiniIcons/question.png';
              }
            }}
          />
        </div>

        {/* Name & Form */}
        <div className="card-name-row">
          <h3 className="card-pokemon-name">{activeMon.name}</h3>
          {activeMon.form && <span className="card-form-tag">({activeMon.form})</span>}
          {isViewingEvolution && (
            <button
              type="button"
              className="card-revert-btn"
              title="Return to base form"
              onClick={(e) => {
                e.stopPropagation();
                setActiveMon(mon);
              }}
            >
              ↩ Base
            </button>
          )}
        </div>

        {/* Type Badges */}
        <div className="card-types-row">
          {activeMon.type1 && (
            <img
              src={getTypeIconSrc(activeMon.type1)}
              alt={activeMon.type1}
              className="card-type-icon"
              title={activeMon.type1}
            />
          )}
          {activeMon.type2 && (
            <img
              src={getTypeIconSrc(activeMon.type2)}
              alt={activeMon.type2}
              className="card-type-icon"
              title={activeMon.type2}
            />
          )}
          {bst > 0 && <span className="card-bst-badge">BST {bst}</span>}
        </div>

        {/* Stats Preview */}
        {activeMon.stats && (
          <div className="card-stats-preview">
            <div className="stat-pill">
              <span className="stat-name">HP</span>
              <span className="stat-val">{activeMon.stats.hp}</span>
            </div>
            <div className="stat-pill">
              <span className="stat-name">ATK</span>
              <span className="stat-val">{activeMon.stats.attack}</span>
            </div>
            <div className="stat-pill">
              <span className="stat-name">DEF</span>
              <span className="stat-val">{activeMon.stats.defense}</span>
            </div>
            <div className="stat-pill">
              <span className="stat-name">SPA</span>
              <span className="stat-val">{activeMon.stats.sp_attack ?? activeMon.stats.specialAttack ?? 0}</span>
            </div>
            <div className="stat-pill">
              <span className="stat-name">SPD</span>
              <span className="stat-val">{activeMon.stats.sp_defense ?? activeMon.stats.specialDefense ?? 0}</span>
            </div>
            <div className="stat-pill">
              <span className="stat-name">SPE</span>
              <span className="stat-val">{activeMon.stats.speed}</span>
            </div>
          </div>
        )}

        {/* Abilities List */}
        <div className="card-abilities-row">
          {activeMon.ability1 && <span className="ability-pill">{activeMon.ability1}</span>}
          {activeMon.ability2 && activeMon.ability2 !== activeMon.ability1 && (
            <span className="ability-pill">{activeMon.ability2}</span>
          )}
          {activeMon.hidden_ability && activeMon.hidden_ability !== activeMon.ability1 && (
            <span className="ability-pill hidden-ab" title="Hidden Ability">
              HA: {activeMon.hidden_ability}
            </span>
          )}
        </div>

        {/* Egg Move Badge */}
        {primaryEgg && (
          <div
            className="card-egg-move-badge"
            title={`Egg Move (Mauville Day Care): ${primaryEgg.name} (${primaryEgg.type} • ${primaryEgg.category}${primaryEgg.power > 0 ? ` • ${primaryEgg.power} Power` : ''})\n${primaryEgg.desc}\nLearned at Mauville Day Care.`}
          >
            <span className="egg-icon">🥚</span>
            <span className="egg-label">Egg:</span>
            <span className="egg-name">{primaryEgg.name}</span>
          </div>
        )}
      </div>

      {/* Built-in Evolution Sidebar */}
      {hasEvolutions && (
        <div className="card-evo-sidebar" onClick={(e) => e.stopPropagation()}>
          <div className="evo-sidebar-label" title="Evolution line: click any sprite to preview on this card">
            EVO
          </div>
          <div className="evo-sidebar-sprites">
            {evoLine.map((evo) => {
              const isCurrent =
                (activeMon.pokedex_id ?? activeMon.id) === (evo.pokedex_id ?? evo.id) &&
                (activeMon.form ?? '') === (evo.form ?? '');
              const isBase =
                (mon.pokedex_id ?? mon.id) === (evo.pokedex_id ?? evo.id) &&
                (mon.form ?? '') === (evo.form ?? '');
              const iconName = getIconName(evo.name, undefined, evo.form);
              const method = evo.evolution_method ? ` (${evo.evolution_method})` : '';
              const tooltip = `${evo.name}${evo.form ? ` [${evo.form}]` : ''}${isBase ? ' [Base Form]' : method}`;

              return (
                <button
                  key={getPokemonKey(evo)}
                  type="button"
                  className={`evo-sprite-btn ${isCurrent ? 'active' : ''} ${isBase ? 'is-base' : ''}`}
                  title={tooltip}
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveMon(evo);
                  }}
                >
                  <img
                    src={`/MiniIcons/${iconName}.png`}
                    alt={evo.name}
                    className="evo-sidebar-icon"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/MiniIcons/question.png';
                    }}
                  />
                  {isBase && <span className="base-indicator-dot" title="Base form" />}
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
};

const SinglePlayerPage: React.FC = () => {
  const navigate = useNavigate();

  const [allPokemon, setAllPokemon] = useState<Pokemon[]>([]);
  const [pool, setPool] = useState<Pokemon[]>([]);
  const [selectedPokemon, setSelectedPokemon] = useState<Pokemon[]>([]);
  const [draftName, setDraftName] = useState('');
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<string | null>(null);

  // Show a temporary toast message
  const showToast = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => {
      setToast((prev) => (prev === msg ? null : prev));
    }, 3200);
  }, []);

  // Roll 12 unique random Pokémon from eligible candidates
  const rollTwelvePokemon = useCallback(
    (sourceList: Pokemon[]): Pokemon[] => {
      // Eligible: base stage, no special obtain method, and withdrawable via notebook
      const candidates = sourceList.filter((p) => {
        const isBase = (p.stage ?? 'base').toLowerCase() === 'base';
        const hasNoSpecialObtain = !p.obtain_method;
        const inNotebook = findMenuIndex(p.name) !== -1;
        return isBase && hasNoSpecialObtain && inNotebook;
      });

      const listToShuffle = candidates.length >= 12 ? candidates : sourceList;
      const shuffled = shuffleArray(listToShuffle);

      // Distinct by key (id + form)
      const selected: Pokemon[] = [];
      const seen = new Set<string>();

      for (const p of shuffled) {
        const key = getPokemonKey(p);
        if (!seen.has(key)) {
          seen.add(key);
          selected.push(p);
          if (selected.length === 12) break;
        }
      }

      return selected;
    },
    []
  );

  // Initial load
  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchPokemonList()
      .then((list) => {
        if (cancelled) return;
        setAllPokemon(list);
        const twelve = rollTwelvePokemon(list);
        setPool(twelve);
        const names = list.map((p) => p.name);
        setDraftName(`Solo - ${generateRandomDraftName(names)}`);
        setLoading(false);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('[SinglePlayer] Failed to load Pokémon list:', err);
        setError('Failed to load Pokémon database. Please try refreshing.');
        setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [rollTwelvePokemon]);

  // Reroll pool
  const handleReroll = () => {
    if (allPokemon.length === 0) return;
    const twelve = rollTwelvePokemon(allPokemon);
    setPool(twelve);
    setSelectedPokemon([]);
    showToast('Rolled 12 new Pokémon!');
  };

  // Toggle selection
  const handleToggleSelect = (pokemon: Pokemon) => {
    const key = getPokemonKey(pokemon);
    const existingIndex = selectedPokemon.findIndex((p) => getPokemonKey(p) === key);

    if (existingIndex >= 0) {
      // Remove
      setSelectedPokemon((prev) => prev.filter((_, i) => i !== existingIndex));
    } else {
      // Add if under 8
      if (selectedPokemon.length >= 8) {
        showToast('You already have 8 Pokémon selected! Deselect one to swap.');
        return;
      }
      setSelectedPokemon((prev) => [...prev, pokemon]);
    }
  };

  // Deselect by slot index
  const handleRemoveFromSlot = (index: number) => {
    setSelectedPokemon((prev) => prev.filter((_, i) => i !== index));
  };

  // Randomly pick 8 from the 12
  const handlePickRandomEight = () => {
    if (pool.length === 0) return;
    const shuffled = shuffleArray(pool);
    setSelectedPokemon(shuffled.slice(0, 8));
    showToast('Auto-selected 8 random Pokémon!');
  };

  // Clear all selections
  const handleClearSelection = () => {
    setSelectedPokemon([]);
  };

  // Start solo run
  const handleStartRun = async () => {
    if (selectedPokemon.length !== 8) {
      showToast(`Please select exactly 8 Pokémon (${selectedPokemon.length}/8 currently).`);
      return;
    }

    setSubmitting(true);
    setError(null);

    const soloPayload = {
      draft_name: draftName.trim() || 'Solo Run',
      pokemon: selectedPokemon.map((p) => ({
        pokedex_id: p.pokedex_id ?? p.id,
        form: p.form ?? null,
      })),
    };

    const params = selectedPokemon
      .map((p) => `pokemon=${encodeURIComponent(p.name.toLowerCase().trim())}`)
      .join('&');

    try {
      const draftId = await createSoloDraft(soloPayload);
      // Navigate to emulator with the draft id, query params for immediate withdraw fallback, and fresh=1
      navigate(`/Emulator/${draftId.trim()}?${params}&fresh=1`);
    } catch (err: any) {
      console.warn('[SinglePlayer] Failed to create solo draft via API, falling back to local emulator URL:', err);
      // Fallback: load directly into the emulator using URL query parameters with unique localId and fresh=1
      const localId = `solo-${Date.now()}`;
      navigate(`/Emulator?${params}&localId=${localId}&fresh=1`);
    } finally {
      setSubmitting(false);
    }
  };

  // Calculate Base Stat Total for a mon
  const calculateBst = (mon: Pokemon): number => {
    if (!mon.stats) return 0;
    const s = mon.stats;
    const spa = s.sp_attack ?? s.specialAttack ?? 0;
    const spd = s.sp_defense ?? s.specialDefense ?? 0;
    return s.hp + s.attack + s.defense + spa + spd + s.speed;
  };

  const selectedKeySet = useMemo(() => {
    return new Set(selectedPokemon.map(getPokemonKey));
  }, [selectedPokemon]);

  return (
    <div className="single-player-page">
      <Header />

      <main className="single-player-main">
        {/* Toast Notification */}
        {toast && <div className="single-player-toast">{toast}</div>}

        {/* Hero Section */}
        <section className="single-player-hero">
          <div className="hero-content">
            <div className="hero-badge">
              <span className="badge-dot"></span>
              Single Player Mode
            </div>
            <h1 className="hero-title">Solo Run Draft</h1>
            <p className="hero-description">
              Roll 12 random Pokémon and pick your favorite <strong>8</strong> to build your starter roster. Once ready, jump straight into the emulator to start your run!
            </p>
          </div>

          {/* Progress / Status Tracker */}
          <div className="hero-status-card">
            <div className="status-header">
              <span className="status-label">Team Selection</span>
              <span className={`status-counter ${selectedPokemon.length === 8 ? 'ready' : ''}`}>
                {selectedPokemon.length} / 8 Selected
              </span>
            </div>
            <div className="status-progress-track">
              <div
                className={`status-progress-fill ${selectedPokemon.length === 8 ? 'complete' : ''}`}
                style={{ width: `${(selectedPokemon.length / 8) * 100}%` }}
              ></div>
            </div>
            <div className="status-subtext">
              {selectedPokemon.length === 8
                ? '✨ Team ready! Click Start Solo Run below.'
                : `Select ${8 - selectedPokemon.length} more Pokémon to begin.`}
            </div>
          </div>
        </section>

        {/* Controls Toolbar */}
        <section className="single-player-toolbar">
          <div className="toolbar-left">
            <div className="draft-name-field">
              <label htmlFor="draft-name-input">Run Name:</label>
              <div className="input-with-button">
                <input
                  id="draft-name-input"
                  type="text"
                  value={draftName}
                  onChange={(e) => setDraftName(e.target.value)}
                  placeholder="e.g. Solo Emerald Blitz"
                  maxLength={40}
                />
                <button
                  type="button"
                  className="refresh-name-btn"
                  onClick={() => {
                    const names = allPokemon.map((p) => p.name);
                    setDraftName(`Solo - ${generateRandomDraftName(names)}`);
                  }}
                  title="Generate new name"
                >
                  ⟳
                </button>
              </div>
            </div>
          </div>

          <div className="toolbar-actions">
            <button
              type="button"
              className="action-btn secondary"
              onClick={handleReroll}
              disabled={loading || submitting}
              title="Generate a brand new pool of 12 Pokémon"
            >
              ⟳ Reroll 12 Pokémon
            </button>
            <button
              type="button"
              className="action-btn secondary"
              onClick={handlePickRandomEight}
              disabled={loading || submitting || pool.length === 0}
              title="Automatically pick 8 random Pokémon from the current pool"
            >
              🎲 Random 8
            </button>
            {selectedPokemon.length > 0 && (
              <button
                type="button"
                className="action-btn secondary danger"
                onClick={handleClearSelection}
                disabled={submitting}
                title="Clear current selection"
              >
                ✕ Clear
              </button>
            )}
            <button
              type="button"
              className={`action-btn primary launch-btn ${selectedPokemon.length === 8 ? 'ready' : ''}`}
              onClick={handleStartRun}
              disabled={selectedPokemon.length !== 8 || submitting}
            >
              {submitting ? (
                <>
                  <span className="spinner"></span> Starting Run…
                </>
              ) : (
                <>🚀 Start Solo Run ({selectedPokemon.length}/8)</>
              )}
            </button>
          </div>
        </section>

        {/* Error notice */}
        {error && <div className="single-player-error">{error}</div>}

        {/* 12 Pokémon Cards Grid */}
        {loading ? (
          <div className="pool-loading-state">
            <div className="spinner large"></div>
            <p>Summoning 12 random Pokémon…</p>
          </div>
        ) : (
          <section className="pool-grid-section">
            <div className="pool-header">
              <h2 className="pool-title">
                Available Pool <span className="pool-count">({pool.length} Pokémon)</span>
              </h2>
              <span className="pool-hint">Click cards to select or deselect</span>
            </div>

            <div className="pool-cards-grid">
              {pool.map((mon, index) => {
                const key = getPokemonKey(mon);
                const isSelected = selectedKeySet.has(key);
                const selectOrder = selectedPokemon.findIndex((p) => getPokemonKey(p) === key) + 1;

                return (
                  <PokemonDraftCard
                    key={key}
                    mon={mon}
                    allPokemon={allPokemon}
                    index={index}
                    isSelected={isSelected}
                    selectOrder={selectOrder}
                    onToggleSelect={handleToggleSelect}
                  />
                );
              })}
            </div>
          </section>
        )}

        {/* Selected Team Roster Tray / Dock */}
        <section className="roster-dock">
          <div className="roster-dock-header">
            <div className="roster-dock-title">
              <h3>Your 8-Pokémon Roster</h3>
              <span className="roster-dock-count">
                {selectedPokemon.length} of 8 picked
              </span>
            </div>
            {selectedPokemon.length === 8 && (
              <button
                type="button"
                className="action-btn primary roster-quick-start"
                onClick={handleStartRun}
                disabled={submitting}
              >
                {submitting ? 'Starting…' : 'Start Run ➔'}
              </button>
            )}
          </div>

          <div className="roster-dock-slots">
            {Array.from({ length: 8 }).map((_, index) => {
              const picked = selectedPokemon[index];

              if (!picked) {
                return (
                  <div key={index} className="roster-slot empty">
                    <span className="slot-number">Slot #{index + 1}</span>
                    <span className="slot-empty-icon">+</span>
                    <span className="slot-empty-text">Empty</span>
                  </div>
                );
              }

              const formattedName = formatPokemonName(picked.name);

              return (
                <div key={getPokemonKey(picked)} className="roster-slot filled">
                  <span className="slot-number">#{index + 1}</span>
                  <button
                    type="button"
                    className="slot-remove-btn"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleRemoveFromSlot(index);
                    }}
                    title={`Remove ${picked.name}`}
                  >
                    ✕
                  </button>
                  <img
                    src={`/MiniIcons/${getIconName(picked.name, undefined, picked.form)}.png`}
                    alt={picked.name}
                    className="slot-sprite"
                    onError={(e) => {
                      (e.target as HTMLImageElement).src = '/MiniIcons/question.png';
                    }}
                  />
                  <span className="slot-pokemon-name">{picked.name}</span>
                  <div className="slot-types">
                    {picked.type1 && (
                      <img
                        src={getTypeIconSrc(picked.type1)}
                        alt={picked.type1}
                        className="slot-type-icon"
                      />
                    )}
                    {picked.type2 && (
                      <img
                        src={getTypeIconSrc(picked.type2)}
                        alt={picked.type2}
                        className="slot-type-icon"
                      />
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default SinglePlayerPage;
